import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";

const ReportsPeriodSummary = () => {
  const [rows, setRows] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPeriodId, setSelectedPeriodId] = useState("");

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const [reportRes, currenciesRes] = await Promise.all([
          apiClient.get("/reports/period-summary"),
          apiClient.get("/currencies"),
        ]);
        const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
        const reportRows = Array.isArray(reportRes.data) ? reportRes.data : [];
        setRows(reportRows);
        setSelectedPeriodId((current) => current || (reportRows[0]?.PeriodId ? String(reportRows[0].PeriodId) : ""));
        setDefaultCurrency(currencyRows.find((row) => Number(row.IsDefault))?.Description || "");
      } catch (err) {
        setError("Failed to load period summary report.");
        setRows([]);
        setSelectedPeriodId("");
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  const selectedRow = useMemo(
    () => rows.find((row) => String(row.PeriodId) === String(selectedPeriodId)) || rows[0] || null,
    [rows, selectedPeriodId]
  );

  const categoryAverageDebits = useMemo(() => {
    if (!selectedRow) return {};
    const selectedIndex = rows.findIndex((row) => String(row.PeriodId) === String(selectedRow.PeriodId));
    const newestFirstWindow = selectedIndex >= 0 ? rows.slice(selectedIndex, selectedIndex + 12) : rows.slice(0, 12);
    const categories = new Set((selectedRow.CategoryExpenditure || []).map((item) => item.Category));

    return Array.from(categories).reduce((acc, category) => {
      const total = newestFirstWindow.reduce((sum, row) => {
        const match = (row.CategoryExpenditure || []).find((item) => item.Category === category);
        return sum + Number(match?.TotalDebits || 0);
      }, 0);
      acc[category] = newestFirstWindow.length > 0 ? total / newestFirstWindow.length : 0;
      return acc;
    }, {});
  }, [rows, selectedRow]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Period Summary Report</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / <Link to="/Reports">Reports</Link> / Period Summary
          </p>
        </div>
      </header>

      {error && <p className="notice notice-error">{error}</p>}
      {loading && <p className="notice notice-muted">Loading period summary...</p>}

      {!loading && (
        <section className="resource-list">
          {rows.length === 0 ? (
            <p className="notice notice-muted">No reporting period data found.</p>
          ) : (
            <>
              <section className="panel list-filter-panel">
                <div className="list-filters">
                  <label className="list-filter-field list-filter-field-compact" htmlFor="period-summary-period">
                    <span>Period</span>
                    <select
                      id="period-summary-period"
                      value={selectedPeriodId}
                      onChange={(e) => setSelectedPeriodId(e.target.value)}
                    >
                      {rows.map((row) => (
                        <option key={row.PeriodId} value={row.PeriodId}>
                          {row.Description}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </section>

              <article key={selectedRow.PeriodId} className="panel">
                <h2>{selectedRow.Description}</h2>
                <div className="dashboard-grid report-summary-strip">
                  <div className="metric-card metric-card-compact">
                    <strong>Total Credits</strong>
                    <div className="resource-meta">{formatMoney(selectedRow.TotalCredits, defaultCurrency || "Default")}</div>
                  </div>
                  <div className="metric-card metric-card-compact">
                    <strong>Total Debits</strong>
                    <div className="resource-meta">{formatMoney(selectedRow.TotalDebits, defaultCurrency || "Default")}</div>
                  </div>
                </div>

                <div className="page-cta">
                  <h3>Category Summary</h3>
                  <div className="period-summary-row period-summary-head">
                    <span>Category</span>
                    <span>Credits</span>
                    <span>Debits</span>
                  </div>
                  <div className="resource-list">
                    {(selectedRow.CategoryExpenditure || []).length === 0 ? (
                      <p className="notice notice-muted">No category activity for this period.</p>
                    ) : (
                      selectedRow.CategoryExpenditure.map((item, idx) => {
                        const periodDebits = Number(item.TotalDebits || 0);
                        const averageDebits = Number(categoryAverageDebits[item.Category] || 0);
                        const maxDebitValue = Math.max(periodDebits, averageDebits, 1);

                        return (
                          <div key={`${selectedRow.PeriodId}-${item.Category}-${idx}`} className="resource-row period-summary-row period-summary-category-row">
                            <strong>{item.Category}</strong>
                            <span className="resource-meta table-money table-number">
                              {formatMoney(item.TotalCredits || 0, defaultCurrency || "Default")}
                            </span>
                            <span className="resource-meta table-money table-number">
                              {formatMoney(periodDebits, defaultCurrency || "Default")}
                            </span>
                            <div className="period-summary-bars" aria-label={`${item.Category} period spend compared with last year average`}>
                              <div className="bar-chart-row period-summary-bar-row">
                                <div className="bar-chart-label">Period</div>
                                <div className="bar-chart-track">
                                  <div
                                    className="bar-chart-fill bar-chart-fill-debit"
                                    style={{ width: `${Math.min((periodDebits / maxDebitValue) * 100, 100)}%` }}
                                  />
                                </div>
                                <div className="bar-chart-value">{formatMoney(periodDebits, defaultCurrency || "Default")}</div>
                              </div>
                              <div className="bar-chart-row period-summary-bar-row">
                                <div className="bar-chart-label">Avg 12 mo</div>
                                <div className="bar-chart-track">
                                  <div
                                    className="bar-chart-fill bar-chart-fill-credit"
                                    style={{ width: `${Math.min((averageDebits / maxDebitValue) * 100, 100)}%` }}
                                  />
                                </div>
                                <div className="bar-chart-value">{formatMoney(averageDebits, defaultCurrency || "Default")}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </article>
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default ReportsPeriodSummary;
